import { useRef } from 'preact/hooks';

import { textInputBlocks, honeyBotTypeName } from '@/constants';
import { makeClassName, tryFocus, nodeListToArray } from '@/utils';

import SelectList from '@/features/elements/components/SelectList';
import Rating from '@/features/elements/components/Rating';
import Contact from '@/features/elements/components/Contact';
import ExternalData from '@/features/elements/components/ExternalData';
import Multimedia from '@/features/elements/components/Multimedia';
import Screenshot from '@/features/elements/components/Screenshot';
import ErrorMessage from '@/features/elements/components/ErrorMessage';
import HoneyBot from '@/features/elements/components/HoneyBot';

import CommentBar from './CommentBar';
import useWcagKeyboardEvents from '../hooks/useWcagKeyboardEvents';
import { showConversationalError } from '../utils';

function findFirstFocusableElement(el) {
	return nodeListToArray(el.querySelectorAll('*')).filter(isFocusable)[0];
}

function isFocusable(element) {
	if (element.tabIndex < 0 || element.offsetParent === null) {
		return false;
	}

	switch (element.tagName) {
		case 'A':
			return !!element.href;
		case 'INPUT':
			return element.type !== 'hidden' && !element.disabled;
		case 'SELECT':
		case 'TEXTAREA':
		case 'BUTTON':
			return !element.disabled;
		default:
			return false;
	}
}

const blockMap = {
	textarea: CommentBar,
	input: CommentBar,
	name: CommentBar,
	email: CommentBar,
	phone: CommentBar,
	radio: SelectList,
	category: SelectList,
	checkbox: SelectList,
	rating: Rating,
	nps: Rating,
	ces: Rating,
	gcr: SelectList,
	thumbs: SelectList,
	contact: Contact, //contact component splitten in -> name, email, telephone, title,
	website_data: ExternalData,
	multimedia: Multimedia,
	screenshot: Screenshot,
	[honeyBotTypeName]: HoneyBot
};

function showErrorComponent(block = {}) {
	if (['email', 'phone'].indexOf(block.typeName) > -1) {
		return true;
	}

	return (
		textInputBlocks.indexOf(block.typeName) > -1 &&
		(block.properties?.required ||
			['email', 'phone'].indexOf(block.properties?.format) > -1)
	);
}

export default function BlockInput({
	state = {},
	block = {},
	errorMessage,
	setValue,
	setNestedValue,
	setHoneyBotValue,
	skipQuestion,
	text,
	elementIndex,
	_id
}) {
	const BlockComponent = blockMap[block.typeName];
	const skipRef = useRef(null);

	const { onKeyDown } = useWcagKeyboardEvents({
		onArrowKeyRight: (e, target) => {
			const el = findFirstFocusableElement(target);
			tryFocus(el);
		}
	});

	if (!BlockComponent) return null;

	// TODO:think about skip style for
	//const showSkip = ['input', 'rating', 'nps', 'ces', 'thumbs','screenshot'].indexOf(block.typeName) > -1 && !block.required;
	const showSkip = !block.properties?.required && block.typeName !== honeyBotTypeName;

	return (
		<div className={makeClassName('block-wrap', showSkip && 'show-skip')}>
			{showSkip && (
				<button
					ref={skipRef}
					className={makeClassName({ prefix: false, className: 'btn' }, 'skip', {
						prefix: false,
						className: 'wcag'
					})}
					onClick={skipQuestion}
					id={`skip-${block.id}`}
					aria-label={`${block.title}, ${text?.skipText ?? 'Skip'}`}
					onKeyDown={e => {
						onKeyDown(e, skipRef?.current?.nextElementSibling);
					}}
				>
					{text?.skipText ?? 'Skip'}
				</button>
			)}
			<div className={makeClassName('block-inner')}>
				<BlockComponent
					block={block}
					state={state}
					setValue={setValue}
					setNestedValue={setNestedValue}
					setHoneyBotValue={setHoneyBotValue}
					skipQuestion={skipQuestion}
					text={text}
					elementIndex={elementIndex}
					_id={_id}
				/>
				{showErrorComponent(block) && (
					<ErrorMessage
						id={`error-message-${block.id}`}
						showErrors={showConversationalError({ block, state })}
					>
						{errorMessage}
					</ErrorMessage>
				)}
			</div>
		</div>
	);
}
